import { AgentHarnessActionTimeoutOutcome, AgentHarnessDueDateInvocationMode } from './agent.harness.settings.enums';

export interface AgentHarnessSettings {
    enabled: boolean;
    policy: {
        debounceSecondsDefault: number;
        cooldownSecondsDefault: number;
        actionTimeoutOutcome: AgentHarnessActionTimeoutOutcome;
        fieldChangedRequireExplicitFields: boolean;
        manual: {
            allowForceBypass: boolean;
            requireAuditReason: boolean;
        };
        dueDateWindow: {
            invocationMode: AgentHarnessDueDateInvocationMode;
            rearmOnExitOnly: boolean;
        };
    };
    behavior: {
        maxGlobalInstructionChars: number;
        maxTriggerInstructionChars: number;
        strictVariableValidationDefault: boolean;
        allowTestRunPreview: boolean;
    };
    guidance: {
        maxMarkdownChars: number;
        strictVariableValidationDefault: boolean;
        requireSummaryOnPublish: boolean;
    };
    circuitBreaker: {
        enabled: boolean;
        openAfterFailures: number;
        failureWindowSeconds: number;
        halfOpenProbeDelaySeconds: number;
        halfOpenJitterPercent: number;
        maxBackoffSeconds: number;
        closeAfterConsecutiveSuccesses: number;
    };
    callbacks: {
        requireHmacSignature: boolean;
        requireReplayProtection: boolean;
        replayWindowSeconds: number;
    };
    adminNotifications: {
        transitionOnly: boolean;
        reminderIntervalSeconds: number;
    };
    inboxNotifications: {
        enabled: boolean;
        actionRequiredDelivery: 'immediate';
        urgentMessageDelivery: boolean;
    };
    logging: {
        outageRollupIntervalSeconds: number;
        perFailureErrorLogs: boolean;
    };
    /** Optional so settings saved before it existed stay valid; `eventRetentionDays` reads the default for them. */
    history?: {
        /** Days an extension run's events (`agent_harness_event`) are kept; a TTL index on their `createdAt` deletes older ones. */
        eventRetentionDays: number;
    };
}

/** Days run events are kept when the tenant has not chosen. */
export const DEFAULT_AGENT_EVENT_RETENTION_DAYS = 90;

export const DEFAULT_AGENT_HARNESS_SETTINGS: AgentHarnessSettings = {
    enabled: false,
    policy: {
        debounceSecondsDefault: 30,
        cooldownSecondsDefault: 300,
        actionTimeoutOutcome: AgentHarnessActionTimeoutOutcome.Failed,
        fieldChangedRequireExplicitFields: true,
        manual: {
            allowForceBypass: true,
            requireAuditReason: true,
        },
        dueDateWindow: {
            invocationMode: AgentHarnessDueDateInvocationMode.OnEntryAndOverdueTransition,
            rearmOnExitOnly: true,
        },
    },
    behavior: {
        maxGlobalInstructionChars: 8000,
        maxTriggerInstructionChars: 4000,
        strictVariableValidationDefault: true,
        allowTestRunPreview: true,
    },
    guidance: {
        maxMarkdownChars: 16000,
        strictVariableValidationDefault: true,
        requireSummaryOnPublish: false,
    },
    circuitBreaker: {
        enabled: true,
        openAfterFailures: 5,
        failureWindowSeconds: 120,
        halfOpenProbeDelaySeconds: 300,
        halfOpenJitterPercent: 20,
        maxBackoffSeconds: 1800,
        closeAfterConsecutiveSuccesses: 2,
    },
    callbacks: {
        requireHmacSignature: true,
        requireReplayProtection: true,
        replayWindowSeconds: 300,
    },
    adminNotifications: {
        transitionOnly: true,
        reminderIntervalSeconds: 21600,
    },
    inboxNotifications: {
        enabled: true,
        actionRequiredDelivery: 'immediate',
        urgentMessageDelivery: true,
    },
    logging: {
        outageRollupIntervalSeconds: 300,
        perFailureErrorLogs: false,
    },
    history: {
        eventRetentionDays: DEFAULT_AGENT_EVENT_RETENTION_DAYS,
    },
};

/** How many days `settings` keeps run events; read by the agent harness index upgrade and the settings command that applies the TTL. */
export function eventRetentionDays(settings: AgentHarnessSettings | null | undefined): number {

    return settings?.history?.eventRetentionDays ?? DEFAULT_AGENT_EVENT_RETENTION_DAYS;
}

export function validateAgentHarnessSettings(settings: AgentHarnessSettings): string[] {

    if (!settings || typeof settings !== 'object') {
        return ['settings must be an object'];
    }

    const errors: string[] = [];

    const validateRange = (value: number, min: number, max: number, label: string): void => {
        if (typeof value !== 'number' || !Number.isFinite(value) || value < min || value > max) {
            errors.push(`${label} must be a number between ${min} and ${max}`);
        }
    };

    const requireObject = (value: unknown, label: string): boolean => {
        if (!value || typeof value !== 'object') {
            errors.push(`${label} is required`);
            return false;
        }

        return true;
    };

    const hasPolicy = requireObject(settings.policy, 'policy');
    requireObject(settings.behavior, 'behavior');
    requireObject(settings.guidance, 'guidance');
    requireObject(settings.circuitBreaker, 'circuitBreaker');
    requireObject(settings.callbacks, 'callbacks');
    requireObject(settings.adminNotifications, 'adminNotifications');
    requireObject(settings.inboxNotifications, 'inboxNotifications');
    requireObject(settings.logging, 'logging');

    if (hasPolicy) {
        requireObject(settings.policy.manual, 'policy.manual');
        requireObject(settings.policy.dueDateWindow, 'policy.dueDateWindow');
    }

    if (errors.length) {
        return errors;
    }

    validateRange(settings.policy.debounceSecondsDefault, 1, 3600, 'policy.debounceSecondsDefault');
    validateRange(settings.policy.cooldownSecondsDefault, 0, 86400, 'policy.cooldownSecondsDefault');
    validateRange(settings.behavior.maxGlobalInstructionChars, 512, 32000, 'behavior.maxGlobalInstructionChars');
    validateRange(settings.behavior.maxTriggerInstructionChars, 256, 16000, 'behavior.maxTriggerInstructionChars');
    validateRange(settings.guidance.maxMarkdownChars, 1024, 64000, 'guidance.maxMarkdownChars');
    validateRange(settings.circuitBreaker.openAfterFailures, 1, 100, 'circuitBreaker.openAfterFailures');
    validateRange(settings.circuitBreaker.failureWindowSeconds, 30, 3600, 'circuitBreaker.failureWindowSeconds');
    validateRange(settings.circuitBreaker.halfOpenProbeDelaySeconds, 30, 3600, 'circuitBreaker.halfOpenProbeDelaySeconds');
    validateRange(settings.circuitBreaker.halfOpenJitterPercent, 0, 50, 'circuitBreaker.halfOpenJitterPercent');
    validateRange(settings.circuitBreaker.maxBackoffSeconds, 60, 86400, 'circuitBreaker.maxBackoffSeconds');
    validateRange(settings.circuitBreaker.closeAfterConsecutiveSuccesses, 1, 10, 'circuitBreaker.closeAfterConsecutiveSuccesses');
    validateRange(settings.callbacks.replayWindowSeconds, 30, 900, 'callbacks.replayWindowSeconds');
    validateRange(settings.adminNotifications.reminderIntervalSeconds, 3600, 86400, 'adminNotifications.reminderIntervalSeconds');
    validateRange(settings.logging.outageRollupIntervalSeconds, 60, 900, 'logging.outageRollupIntervalSeconds');
    if (settings.history) {
        validateRange(settings.history.eventRetentionDays, 1, 3650, 'history.eventRetentionDays');
    }

    if (settings.behavior.maxTriggerInstructionChars > settings.behavior.maxGlobalInstructionChars) {
        errors.push('behavior.maxTriggerInstructionChars must be less than or equal to behavior.maxGlobalInstructionChars');
    }

    return errors;
}
